import { useEffect, useRef, useState } from "react";

// Walk up from the anchor to find the nearest vertically-scrollable ancestor.
const getScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const NEAR_BOTTOM_THRESHOLD = 80;

// Hook to scroll the chat container to the bottom when dependencies change.
const useChatScroll = (
  ref: React.RefObject<HTMLDivElement>,
  deps: React.DependencyList,
  forceScroll = false
) => {
  const isNearBottomRef = useRef(true);

  const forceScrollRef = useRef(forceScroll);
  useEffect(() => {
    forceScrollRef.current = forceScroll;
  }, [forceScroll]);

  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (container) return;
    const resolved = getScrollContainer(ref.current);
    if (resolved) setContainer(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, container, ...deps]);

  useEffect(() => {
    if (!container) return;

    const updateNearBottom = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      isNearBottomRef.current = distanceFromBottom < NEAR_BOTTOM_THRESHOLD;
    };

    updateNearBottom();
    container.addEventListener("scroll", updateNearBottom, { passive: true });
    return () => container.removeEventListener("scroll", updateNearBottom);
  }, [container]);

  useEffect(() => {
    const anchor = ref.current;
    const container = getScrollContainer(anchor);
    if (!anchor) return;
    if (!container) {
      anchor.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }

    if (forceScrollRef.current || isNearBottomRef.current) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, deps);
};

export default useChatScroll;
